// Resources view (wireframe 08) — three modes: Utilization (bar chart of
// mean busy %), Bottleneck (cp_resource_diagram iframe with bottleneck
// analysis), Roster (table). Right pin pane shows selected resource +
// click-driven detail.

import { useMemo, useState } from "react";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

type Mode = "utilization" | "bottleneck" | "roster";

const MODES: Array<{ id: Mode; label: string }> = [
  { id: "utilization", label: "Utilization" },
  { id: "bottleneck",  label: "Bottleneck" },
  { id: "roster",      label: "Roster" },
];

interface ResourceRow {
  id: string;
  name: string;
  role: string | null;
  rate: number | null;
  task_count: number;
  total_task_days: number;
}

export default function ResourcesView() {
  const { project } = useShell();
  const [mode, setMode] = useState<Mode>("utilization");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = useTool("list_resources", { project });
  const diagram = useTool("render_resource_diagram", { project });
  const rows = ((list.env?.data as { rows?: ResourceRow[] } | undefined)?.rows ?? []) as ResourceRow[];

  // Utilisation as mean busy days / max span (max total_task_days across the roster).
  const enriched = useMemo(() => {
    const maxDays = Math.max(1, ...rows.map((r) => r.total_task_days ?? 0));
    return rows.map((r) => ({
      ...r,
      mean_busy_pct: r.total_task_days > 0 ? r.total_task_days / maxDays : 0,
    }));
  }, [rows]);

  const sortedByLoad = useMemo(
    () => [...enriched].sort((a, b) => b.mean_busy_pct - a.mean_busy_pct),
    [enriched],
  );

  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;
  const iframeUrl = (diagram.env?.data as { url?: string } | undefined)?.url ?? null;

  useRightPane(
    useMemo(() => (
      <RightPane selected={selected} onClear={() => setSelectedId(null)} />
    ), [selected]),
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Mode toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {rows.length} resource{rows.length === 1 ? "" : "s"}
          {selectedId ? ` · selected: ${selectedId}` : ""}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5 text-xs">
          {MODES.map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)}
                    className={
                      "rounded px-3 py-1 " +
                      (mode === m.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")
                    }>{m.label}</button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-auto">
        {list.loading ? <Skeleton lines={6} /> :
         list.error ? <ErrorState error={list.error} onRetry={list.refetch} /> :
         mode === "utilization" ? (
            <UtilizationView rows={sortedByLoad} selectedId={selectedId} onSelect={setSelectedId} />
         ) : mode === "bottleneck" ? (
            <BottleneckView loading={diagram.loading} error={diagram.error}
                            refetch={diagram.refetch} url={iframeUrl} />
         ) : (
            <RosterView rows={rows} selectedId={selectedId} onSelect={setSelectedId} />
         )}
      </div>
    </div>
  );
}

function UtilizationView({ rows, selectedId, onSelect }: {
  rows: Array<ResourceRow & { mean_busy_pct: number }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!rows.length) {
    return <div className="p-4 text-sm text-slate-500">No resources in canonical.</div>;
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">Top-loaded resources (mean busy %)</div>
      <ul className="space-y-2 text-xs">
        {rows.map((r) => {
          const pct = Math.round(r.mean_busy_pct * 100);
          const color = pct >= 90 ? "bg-rose-500"
                      : pct >= 70 ? "bg-amber-500"
                      : pct >= 40 ? "bg-sky-500"
                      :             "bg-emerald-500";
          const active = r.id === selectedId;
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r.id)}
                className={
                  "block w-full text-left transition-colors " +
                  (active ? "rounded bg-sky-50 px-2 py-1 ring-1 ring-sky-300" : "")
                }
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{r.name}</span>
                  <span className={pct >= 90 ? "text-rose-600" : pct >= 70 ? "text-amber-600" : "text-slate-700"}>
                    {pct}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded bg-slate-100">
                  <div className={`h-full rounded ${color}`} style={{ width: `${Math.max(2, pct)}%` }} />
                </div>
                <div className="mt-0.5 text-[10px] text-slate-500">
                  {r.task_count} task{r.task_count === 1 ? "" : "s"} · {r.total_task_days}d total
                  {r.rate ? ` · $${r.rate}/h` : ""}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BottleneckView({ loading, error, refetch, url }: {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  url: string | null;
}) {
  if (loading) return <Skeleton lines={4} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!url) return <div className="p-4 text-sm text-slate-500">No resource-bottleneck report available.</div>;
  // key={url} forces React to remount the iframe whenever the URL changes
  // (instead of swapping `src` on the same DOM node and racing the previous
  // load — see Bug #8, 2026-05-20). `&_=Date.now()` busts any half-cached
  // response from an earlier request.
  const iframeSrc =
    url + (url.includes("?") ? "&" : "?") + "chrome=false&_=" + Date.now();
  return (
    <div className="h-full min-h-[500px] overflow-hidden rounded-lg border border-slate-200 bg-white">
      <iframe key={url}
              src={iframeSrc}
              title="Resource bottleneck"
              className="h-full w-full min-h-[500px] border-0" />
    </div>
  );
}

function RosterView({ rows, selectedId, onSelect }: {
  rows: ResourceRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!rows.length) {
    return <div className="p-4 text-sm text-slate-500">No resources in canonical.</div>;
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">Roster ({rows.length})</div>
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr className="border-b border-slate-100">
            <th className="text-left font-normal">id</th>
            <th className="text-left font-normal">name</th>
            <th className="text-right font-normal">$/h</th>
            <th className="text-right font-normal">tasks</th>
            <th className="text-right font-normal">task days</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const active = r.id === selectedId;
            return (
              <tr
                key={r.id}
                className={
                  "cursor-pointer border-b border-slate-50 last:border-b-0 " +
                  (active ? "bg-sky-50" : "hover:bg-slate-50")
                }
                onClick={() => onSelect(r.id)}
              >
                <td className="py-1.5 pr-2 font-mono text-sky-700">{r.id}</td>
                <td className="py-1.5 pr-2 text-slate-800">{r.name}</td>
                <td className="py-1.5 pr-2 text-right">{r.rate == null ? "—" : `$${r.rate}`}</td>
                <td className="py-1.5 pr-2 text-right">{r.task_count}</td>
                <td className="py-1.5 text-right">{r.total_task_days}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RightPane({ selected, onClear }: {
  selected: ResourceRow | null;
  onClear: () => void;
}) {
  if (!selected) {
    return (
      <div className="rounded border border-dashed border-slate-200 p-3 text-xs text-slate-500">
        Click a resource (Utilization or Roster mode) to see its details here.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <section>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Selected resource</div>
          <button type="button" onClick={onClear}
                  className="text-[10px] text-slate-400 hover:text-slate-600">clear</button>
        </div>
        <div className="mt-2 rounded border border-slate-200 p-2 text-xs">
          <div className="font-medium">{selected.name}</div>
          <dl className="mt-2 grid grid-cols-[80px_minmax(0,1fr)] gap-y-1 text-[11px]">
            <dt className="text-slate-500">id</dt><dd className="font-mono">{selected.id}</dd>
            <dt className="text-slate-500">rate</dt>
            <dd>{selected.rate == null ? "—" : `$${selected.rate}/h`}</dd>
            <dt className="text-slate-500">tasks</dt><dd>{selected.task_count}</dd>
            <dt className="text-slate-500">task days</dt><dd>{selected.total_task_days}</dd>
          </dl>
        </div>
      </section>
    </div>
  );
}
